/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessSetDirectoryRoleMappings } from "../funcs/accessSetDirectoryRoleMappings.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DirectoryRoleMapping } from "../models/components/directoryrolemapping.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetDirectoryRoleMappingsRequest,
  SetDirectoryRoleMappingsSecurity,
} from "../models/operations/setdirectoryrolemappings.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetDirectoryRoleMappingsMutationVariables = {
  request: SetDirectoryRoleMappingsRequest;
  security?: SetDirectoryRoleMappingsSecurity | undefined;
  options?: RequestOptions;
};

export type SetDirectoryRoleMappingsMutationData = Array<DirectoryRoleMapping>;

export type SetDirectoryRoleMappingsMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setDirectoryRoleMappings access
 *
 * @remarks
 * Replace the full set of roles granted by a directory group or attribute value. An empty role list removes all mappings for the source.
 */
export function useSetDirectoryRoleMappingsMutation(
  options?: MutationHookOptions<
    SetDirectoryRoleMappingsMutationData,
    SetDirectoryRoleMappingsMutationError,
    SetDirectoryRoleMappingsMutationVariables
  >,
): UseMutationResult<
  SetDirectoryRoleMappingsMutationData,
  SetDirectoryRoleMappingsMutationError,
  SetDirectoryRoleMappingsMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetDirectoryRoleMappingsMutation(client, options),
    ...options,
  });
}

export function mutationKeySetDirectoryRoleMappings(): MutationKey {
  return ["@gram/client", "access", "setDirectoryRoleMappings"];
}

export function buildSetDirectoryRoleMappingsMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetDirectoryRoleMappingsMutationVariables,
  ) => Promise<SetDirectoryRoleMappingsMutationData>;
} {
  return {
    mutationKey: mutationKeySetDirectoryRoleMappings(),
    mutationFn: function setDirectoryRoleMappingsMutationFn({
      request,
      security,
      options,
    }): Promise<SetDirectoryRoleMappingsMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessSetDirectoryRoleMappings(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
